import type { ReactNode } from 'react';
import React from 'react';

import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import type { CollapsibleCardProps } from '~/components/core/Molecules/Collapsible/types';
import type { GetChevronFunc } from '~/components/core/Molecules/Collapsible/useCollapsible';
import { useCollapsible } from '~/components/core/Molecules/Collapsible/useCollapsible';
import Collapse from '~/components/ui/atoms/Collapse';

import cn from '../../../../Utils/cn';
import { Text } from '../../TextComponents';

import type { CardBaseExternalProps, CardBaseStyleProps, TitleProps } from './types';
import { getCardContentContainerSpacingClass, getCardPaddingClasses } from './types';

const BORDER_CLASS = 'border-1 border-slate-600 border-solid';

interface CardBaseProps extends CollapsibleCardProps, CardBaseExternalProps, CardBaseStyleProps {
  titleProps: TitleProps;
  titleAction?: ReactNode;
  titleClassName?: string;
  /**
   * Classes for the row holding the chevron, title and title action. Merged over the defaults, so
   * `items-start` top-aligns the whole row — a card whose title block is several lines tall should
   * not centre its avatar against it. Omit to keep the centred default.
   */
  titleRowClassName?: string;
}

export const CardBase: React.FC<React.PropsWithChildren<CardBaseProps>> = ({
  className,
  paddingX,
  paddingY,
  shadow,
  border,
  backgroundColor,
  contentGap,
  titleProps,
  collapsible,
  onCollapseClick,
  isOpen,
  openByDefault,
  title,
  action,
  children,
  titleAction,
  titleClassName,
  titleRowClassName,
  direction = 'left',
}) => {
  const { onClick, isOpenCombined, getChevron } = useCollapsible({
    collapsible,
    isOpen,
    openByDefault,
    onCollapseClick,
  });

  const shouldShowAction = action && (!collapsible || isOpenCombined);
  const toggleLabel = typeof title === 'string' && title ? title : TOGGLE_FALLBACK_LABEL;
  const shouldShowTitleRow = collapsible || title || action;

  const getTextProps = () => ({
    variant: titleProps.variant ? Text.VARIANTS[titleProps.variant] : undefined,
    weight: titleProps.weight ? Text.WEIGHTS[titleProps.weight] : undefined,
    colorVariant: titleProps.colorVariant ? Text.COLOR_VARIANTS[titleProps.colorVariant] : undefined,
  });

  const spacingClasses = getCardContentContainerSpacingClass(contentGap, Boolean(title));
  const isArrowPositionLeft = direction === 'left';

  const ActionComponent = shouldShowAction ? (
    <div className="flex w-full items-center justify-end">{action}</div>
  ) : null;

  return (
    <CardFrame
      className={className}
      paddingX={paddingX}
      paddingY={paddingY}
      shadow={shadow}
      border={border}
      backgroundColor={backgroundColor}
    >
      {shouldShowTitleRow ? (
        <div className={cn('row-auto flex items-center justify-between', { 'cursor-pointer': collapsible })}>
          <div className={cn('row-auto flex w-full items-center gap-12', titleRowClassName)} onClick={onClick}>
            {isArrowPositionLeft ? (
              <Arrow collapsible={collapsible} getChevron={getChevron} isOpen={isOpenCombined} label={toggleLabel} />
            ) : titleAction ? (
              <div className="flex items-center">{titleAction}</div>
            ) : null}
            {title ? (
              <div className={cn('flex w-full items-center', titleClassName)}>
                <Text className="w-full" {...getTextProps()}>
                  {title}
                </Text>
              </div>
            ) : null}
            {!isArrowPositionLeft ? (
              <>
                {ActionComponent}
                <Arrow collapsible={collapsible} getChevron={getChevron} isOpen={isOpenCombined} label={toggleLabel} />
              </>
            ) : titleAction ? (
              <div className="flex items-center">{titleAction}</div>
            ) : null}
          </div>
          {isArrowPositionLeft ? ActionComponent : null}
        </div>
      ) : null}
      {collapsible ? (
        <Collapse in={isOpenCombined} timeout="auto" unmountOnExit>
          <div className={`flex flex-col ${spacingClasses}`}>{children}</div>
        </Collapse>
      ) : (
        <div className={`flex flex-col ${spacingClasses}`}>{children}</div>
      )}
    </CardFrame>
  );
};

// Names the chevron button when the title is not plain text and so cannot name it.
const TOGGLE_FALLBACK_LABEL = 'Toggle section';

interface ArrowProps {
  collapsible?: boolean;
  getChevron: GetChevronFunc;
  isOpen: boolean;
  label: string;
}

const Arrow: React.FC<ArrowProps> = ({ collapsible, getChevron, isOpen, label }) => {
  return collapsible ? (
    <IconButton size="small" className="h-32 w-32" aria-label={label} aria-expanded={isOpen}>
      {getChevron({ className: 'h-[22px] w-[22px]' })}
    </IconButton>
  ) : null;
};

export const CardFrame: React.FC<React.PropsWithChildren<Omit<CardBaseStyleProps, 'contentGap'>>> = ({
  className,
  paddingX,
  paddingY,
  shadow,
  border,
  backgroundColor,
  children,
}) => (
  <div
    className={cn(
      backgroundColor,
      getCardPaddingClasses(paddingX, paddingY),
      'flex w-full flex-col rounded-md',
      {
        [BORDER_CLASS]: border,
        'shadow-sm': shadow,
      },
      className
    )}
  >
    {children}
  </div>
);
